import { useDealContext } from "../features/deal";
import { useBrandContext } from "../hooks/useBrandContext";
import { useFirmContext } from "../features/firm/context/FirmContext";
import { useUserContext } from "../features/users";
import { Box, Stack, Button, Typography, Menu, MenuItem, Divider, Fade } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import logo from "../assets/images/brightloop-logo-std-light-mode.svg";
import { Link, useNavigate } from "react-router-dom";
import { IProject, IOrganization } from "../types";
import { TransitionOverlay, useTransition } from "./TransitionOverlay";
import ClientProvider from "../modules/ClientProvider";
import { useAuth0 } from "@auth0/auth0-react";

const RoleLabels = {
    "admin": "Admin",
    "contributor": "Contributor",
    "advisor": "Advisor",
    "guest": "Guest"
}

const BrandMenuItem = ({ tag, name, logo_url, icon_url, type, role, link, onClose, onTransition }: { 
    tag: string, 
    name: string, 
    logo_url: string | undefined, 
    icon_url: string | undefined, 
    type: string, 
    role: string, 
    link: string, 
    onClose: () => void,
    onTransition: (message: string) => Promise<void>
}) => {
    const navigate = useNavigate();

    // Preload the destination page
    useEffect(() => {
        if (link.startsWith('http://') || link.startsWith('https://') || link.includes('://')) {
            // Create a prefetch link for external domains
            const linkEl = document.createElement('link');
            linkEl.rel = 'dns-prefetch';
            linkEl.href = link;
            document.head.appendChild(linkEl);

            return () => {
                if (document.head.contains(linkEl)) {
                    document.head.removeChild(linkEl);
                }
            };
        }
    }, [link]);

    const handleClick = async (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent default navigation
        onClose();
        
        // Check if it's an external link (contains protocol)
        if (link.startsWith('http://') || link.startsWith('https://') || link.includes('://')) {
            // Show loading state for external links (subdomain changes)
            await onTransition('');
            window.location.href = link;
        } else {
            navigate(link);
        }
    };

    return (
        <MenuItem 
            component="a"
            href={link}
            sx={{ 
                padding: "8px 16px",
                cursor: "pointer",
                transition: "all 0.15s ease-in-out",
                textDecoration: "none",
                color: "inherit",
                "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    transform: 'translateX(4px)',
                    textDecoration: "none"
                }
            }}
            onClick={handleClick}
        >
            <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%" }}>
                <Box sx={{ 
                    maxHeight: '22px', 
                    maxWidth: '140px', 
                    width: "22px", 
                    height: "22px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    transition: "all 0.15s ease-in-out"
                }}>
                    {icon_url ?
                        <Box component="img" src={icon_url} alt="Project Logo" sx={{ maxHeight: '22px', borderRadius: "5px" }} />
                        : <Box sx={{ maxHeight: '22px', maxWidth: '140px', width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.05)", borderRadius: "5px" }}>
                            <Typography variant="body1" sx={{ fontSize: "1rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "200px", color: "text.primary", fontWeight: "bold" }}>{name.charAt(0)}</Typography>
                        </Box>
                    }
                </Box>
                <Typography variant="body1" sx={{ fontSize: "1rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "200px", color: "text.primary", fontWeight: "bold" }}>
                    <strong>{name}</strong>
                </Typography>
            </Stack>
        </MenuItem>
    );
}

const generateIconUrl = (domain: string) => {
    if (!domain) {
        return undefined;
    }
    return `https://www.google.com/s2/favicons?domain=${domain.replace("https://", "").replace("http://", "").replace("www.", "").replace("app.", "").replace("/.*", "")}&sz=128`;
}

export const BrandSelector = () => {
    const { brandContext } = useBrandContext();
    const { brand } = brandContext;
    const { userContext } = useUserContext();
    const { firmContext } = useFirmContext();
    const { dealContext } = useDealContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { isTransitioning, transitionMessage, startTransition } = useTransition();
    const menuOpen = Boolean(anchorEl);
    const [ openToAdvising, setOpenToAdvising ] = useState(userContext.dbUser?.open_to_advising ?? false);

    // Sync local state with user context updates
    useEffect(() => {
        setOpenToAdvising(userContext.dbUser?.open_to_advising ?? false);
    }, [userContext.dbUser?.open_to_advising]);

    const { getAccessTokenSilently } = useAuth0();
    const userHttpClient = ClientProvider.provideUserClient();

    const enableSelect = !brand?.tag || userContext?.projectMemberships?.some((p: any) => p.role === "advisor") || userContext?.organizationMemberships?.length > 0 || userContext?.projectMemberships?.length > 1;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const companies = userContext.projectMemberships.filter((p: any) => ["contributor", "admin"].includes(p.role)).map((p: any) => p.project).sort((a: any, b: any) => a.name.localeCompare(b.name));
    const firms = userContext.organizationMemberships.filter((o: any) => ["contributor", "admin"].includes(o.role)).map((o: any) => o.organization).sort((a: any, b: any) => a.name.localeCompare(b.name));
    const advisories = userContext.projectMemberships.filter((p: any) => p.role === "advisor").map((p: any) => p.project).sort((a: any, b: any) => a.name.localeCompare(b.name));



    const logoUrl = brand && brand.tag ? brand.logo_url : logo.toString();

    if (!enableSelect) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%', padding: "0 10px" }}>
                {logoUrl ?
                    <Box component="img" src={logoUrl} alt="Project Logo" sx={{ maxHeight: '34px' }} />
                    :
                    <h5>{brand?.name}</h5>
                }
            </Box>
        );
    }




    const brandIcon = generateIconUrl(brand?.website ?? "");

    return (
        <Box>
            <Button
                onClick={handleClick}
                endIcon={
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        {menuOpen ? <ChevronUp size={16} color="#667788" /> : <ChevronDown size={16} color="#667788" />}
                    </Box>
                }
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: "5px",
                    padding: { xs: "8px 8px", md: "10px 10px" },
                    backgroundColor: "#f8f9fa",
                    color: 'inherit',
                    textTransform: 'none',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease-in-out',
                    minWidth: { xs: "100%", md: "64px" },
                    "&:hover": {
                        backgroundColor: "#f8f9fa99",
                        textDecoration: 'none',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                }}
            >

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%", gap: "10px" }}>
                    {/* Mobile view - always show brandIcon */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: "center", justifyContent: "center" }}>
                        <Box sx={{ maxHeight: '22px', maxWidth: '140px', borderRadius: "5px", width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {brandIcon ?
                                <Box component="img" src={brandIcon} alt="Project Logo" sx={{ maxHeight: '22px', borderRadius: "5px" }} />
                                :
                                <Box sx={{ maxHeight: '22px', maxWidth: '140px', width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.05)", borderRadius: "5px" }}>
                                    <Typography variant="h6" sx={{ fontSize: "1rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "200px", color: "text.primary", fontWeight: "bold" }}>{brand?.name.charAt(0)}</Typography>
                                </Box>
                            }
                        </Box>
                    </Box>
                    
                    {/* Desktop view - show logoUrl if available, otherwise brandIcon + text */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: "center", justifyContent: "flex-start", width: "100%", gap: "10px" }}>
                        {logoUrl ?
                            <Box component="img" src={logoUrl} alt="Project Logo" sx={{ maxHeight: '28px', maxWidth: '145px' }} />
                            :
                            <>
                                <Box sx={{ maxHeight: '22px', maxWidth: '140px', borderRadius: "5px", width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {brandIcon ?
                                        <Box component="img" src={brandIcon} alt="Project Logo" sx={{ maxHeight: '22px', borderRadius: "5px" }} />
                                        :
                                        <Box sx={{ maxHeight: '22px', maxWidth: '140px', width: "22px", height: "22px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.05)", borderRadius: "5px" }}>
                                            <Typography variant="h6" sx={{ fontSize: "1rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "200px", color: "text.primary", fontWeight: "bold" }}>{brand?.name.charAt(0)}</Typography>
                                        </Box>
                                    }
                                </Box>
                                <Typography variant="h6" sx={{ fontSize: "1rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "115px", color: "text.primary", fontWeight: "bold", textAlign: "left" }}>{brand?.name}</Typography>
                            </>
                        }
                    </Box>
                </Box>
            </Button >

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: '350px',
                        maxHeight: '400px',
                        borderRadius: "10px",
                        mt: 1,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.08)'
                    }
                }}
                TransitionComponent={Fade}
                transitionDuration={200}
            >
                <Box>
                    {brand && (
                        <Box sx={{ padding: '8px 0' }}>
                            <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ padding: "0 16px"}}>
                                <strong>Currently Viewing:</strong>
                            </Typography>
                            <Box sx={{ padding: '8px 16px' }}>
                                <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%" }}>
                                    <Box sx={{ maxHeight: '32px', maxWidth: '140px', width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}>

                                        {brandIcon ?
                                            <Box component="img" src={brandIcon} alt="Project Logo" sx={{ maxHeight: '32px', borderRadius: "5px" }} />

                                            : <Box sx={{ maxHeight: '32px', maxWidth: '140px', width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.05)", borderRadius: "5px" }}>
                                                <Typography variant="body1" sx={{ fontSize: "1.2rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", color: "text.primary", fontWeight: "bold" }}>{brand?.name.charAt(0)}</Typography>
                                            </Box>
                                        }
                                    </Box>
                                    <Typography variant="body1" sx={{ fontSize: "1.2rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "200px", color: "text.primary", fontWeight: "bold" }}>
                                        <strong>{brand?.name}</strong>
                                    </Typography>

                                </Stack>
                            </Box>
                        </Box>
                    )}
                    {advisories.length > 0 && (
                        <>
                            { (brand) && <Divider sx={{ margin: "8px 0" }} /> }

                            <Box sx={{ padding: '8px 0' }}>
                                <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ padding: "0 16px"}}>
                                    <strong>Your Advisory Roles</strong>
                                </Typography>
                                {advisories.map((project: IProject, index: number) => {
                                    return (
                                        <BrandMenuItem key={`advisory-${index}`} tag={project.tag} name={project.name} logo_url={project.logo_url} icon_url={generateIconUrl(project.company?.domain ?? "")} type="project" role="advisor" onClose={handleClose} onTransition={startTransition} link={`${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${project.tag}.${process.env.REACT_APP_APP_ORIGIN}/dashboard`} />
                                    )
                                })}
                            </Box>
                            {/* Status indicator for advising availability */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',  borderRadius: '8px', margin: '8px 22px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{ 
                                        width: '8px', 
                                        height: '8px', 
                                        borderRadius: '50%', 
                                        backgroundColor: openToAdvising ? '#4caf50' : '#ff9800' 
                                    }} />
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        {openToAdvising ? 'Open to additional advisory roles' : 'Not currently open to additional roles'}
                                    </Typography>
                                </Box>
                                <Button 
                                    component={Link}
                                    to={`//app.${process.env.REACT_APP_APP_ORIGIN}/settings/preferences`}
                                    size="small"
                                    sx={{ 
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        minWidth: 'auto',
                                        padding: '4px 8px'
                                    }}
                                >
                                    Edit
                                </Button>
                            </Box>
                        </>
                    )}
                    {firms.length > 0 && (
                        <>
                            { (brand || advisories.length > 0) && <Divider sx={{ margin: "8px 0" }} /> }

                            <Box sx={{ padding: '8px 0' }}>
                                <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ padding: "0 16px"}}>
                                    <strong>Your Firms</strong>
                                </Typography>
                                {firms.map((organization: IOrganization, index: number) => {
                                    const link = organization.experience === "advisor" ? `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${organization.tag}.${process.env.REACT_APP_APP_ORIGIN}/dashboard`
                                        : `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://app.${process.env.REACT_APP_APP_ORIGIN}/o/${organization.tag}`;
                                    return (
                                        <BrandMenuItem key={`firm-${index}`} tag={organization.tag} name={organization.name} logo_url={organization.logo_url} icon_url={generateIconUrl(organization?.website ?? "")} type="organization" role="contributor" onClose={handleClose} onTransition={startTransition} link={link} />
                                    )
                                })}
                            </Box>
                        </>
                    )}
                    {companies.length > 0 && (
                        <>
                            { (brand || advisories.length > 0 || firms.length > 0) && <Divider sx={{ margin: "8px 0" }} /> }

                            <Box sx={{ padding: '8px 0' }}>
                                <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ padding: "0 16px"}}>
                                    <strong>Your Companies</strong>
                                </Typography>
                                {companies.map((project: IProject, index: number) => {
                                    const link = project.experience === "advisor" ? `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${project.tag}.${process.env.REACT_APP_APP_ORIGIN}/dashboard`
                                        : project.experience === "winning" ? `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://app.${process.env.REACT_APP_APP_ORIGIN}/o/${project.organization.tag}/d/${project.tag}`
                                            : `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://app.${process.env.REACT_APP_APP_ORIGIN}/o/${project.organization.tag}/project/${project.tag}`;

                                    return (
                                        <BrandMenuItem
                                            key={`project-${index}`}
                                            tag={project.tag}
                                            name={project.name}
                                            logo_url={project.logo_url}
                                            icon_url={generateIconUrl(project.company?.domain ?? "")}
                                            type="project"
                                            role="contributor"
                                            onClose={handleClose}
                                            onTransition={startTransition}
                                            link={link}
                                        />
                                    )
                                })}

                            </Box>
                        </>
                    )}


                </Box>


            </Menu>

            <TransitionOverlay 
                open={isTransitioning} 
                message={transitionMessage}
                subMessage=""
            />
        </Box >
    );
}